import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Switch } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SavingsIcon from '@mui/icons-material/Savings';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { getMachineProgramSummary, getExcessReasonBreakdown, getAlarmBreakdown, getTopIdleProgram } from '../../../Services/app/oeeservice';

const COLOR_RED = '#d03b3b';
const COLOR_AMBER = '#e0a11a';
const COLOR_ORANGE = '#eb6834';
const COLOR_GREEN = '#0ca30c';
const COLOR_BLUE = '#2a78d6';
const COLOR_PURPLE = '#7e4fd1';
const NAVY = '#0d2a5c';
const BORDER = '#ECEFF3';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_MUTED = '#898781';

const StatusBox = ({ children }) => (
  <Box
    sx={{
      p: 6,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: TEXT_MUTED,
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const secondsToHours = (totalSeconds) => (Number(totalSeconds) || 0) / 3600;
const fmtHrs = (totalSeconds) => `${secondsToHours(totalSeconds).toFixed(1)} hrs`;
const fmtCost = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const secondsToHms = (totalSeconds) => {
  const abs = Math.abs(Math.round(Number(totalSeconds) || 0));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};
const clampPercent = (val) => Math.max(0, Math.min(100, val));

const IconCircle = ({ icon, color, size = 44 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `${color}1a`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {icon}
  </Box>
);

const formatMachineLabel = (machines) => {
  if (!Array.isArray(machines) || machines.length === 0) return '';
  if (machines.length <= 2) return machines.join(', ');
  return `${machines.slice(0, 2).join(', ')} +${machines.length - 2} more`;
};

const RecoveryHero = ({ totals, periodLabel, customerName, machines, idleTimeValue, idleCostValue, showNonMandatoryNet }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${NAVY} 0%, #123a7a 100%)`,
      borderRadius: 2,
      color: '#fff',
      p: 3,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 3,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '2 1 340px' }}>
      <IconCircle icon={<SavingsIcon />} color="#8fd7ff" size={56} />
      <Box>
        <Typography sx={{ fontSize: '11px', letterSpacing: '0.06em', color: '#b8c6e6', fontWeight: 600 }}>
          THIS {periodLabel ? periodLabel.toUpperCase() : ''} RECOVERY OPPORTUNITY
        </Typography>
        {(customerName || (machines && machines.length > 0)) && (
          <Typography sx={{ fontSize: '11px', color: '#8fd7ff', fontWeight: 600, mb: 0.25 }}>
            {[customerName, formatMachineLabel(machines)].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Typography sx={{ fontSize: '34px', fontWeight: 700, color: '#8fe08f', lineHeight: 1.2 }}>
          {fmtCost(idleCostValue)}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#dfe6f5' }}>
          {showNonMandatoryNet ? 'Total Non Mandatory Idle Cost (Net), by reason' : 'Total Non Mandatory Idle Cost1 (Net), by part'}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#b8c6e6', mt: 0.5 }}>
          {showNonMandatoryNet
            ? 'net every reason below its configured threshold to recover this'
            : 'net every part\'s idle below its target handling time to recover this'}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 3, flex: '1 1 260px', borderLeft: { sm: '1px solid #3a568f' }, pl: { sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <AccessTimeIcon sx={{ color: '#8fd7ff', fontSize: 20, mt: 0.3 }} />
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{fmtHrs(idleTimeValue)}</Typography>
          <Typography sx={{ fontSize: '11px', color: '#b8c6e6' }}>
            {showNonMandatoryNet ? 'Non Mandatory Idle Time (Net)' : 'Non Mandatory Idle Time1 (Net)'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Inventory2OutlinedIcon sx={{ color: '#8fd7ff', fontSize: 20, mt: 0.3 }} />
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{totals.additional_parts} parts</Typography>
          <Typography sx={{ fontSize: '11px', color: '#b8c6e6' }}>Additional production opportunity</Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

const StatCard = ({ icon, color, title, value, valueColor, subtitle, footnote }) => (
  <Box
    sx={{
      flex: '1 1 220px',
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1,
    }}
  >
    <IconCircle icon={icon} color={color} />
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: TEXT_PRIMARY }}>{title}</Typography>
    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: valueColor || color }}>{value}</Typography>
    <Typography sx={{ fontSize: '12px', color: TEXT_MUTED }}>{subtitle}</Typography>
    {footnote && (
      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: valueColor || color }}>{footnote}</Typography>
    )}
  </Box>
);

// Same table design as OverviewPage2.js's ManagementActions (Method 1/2/3's
// Overview page 2) - Priority/Issue/Impact/Recommended Action columns with a
// numbered color circle, reused here for Method 4's own attention items.
const ManagementActionsTable = ({ rows }) => (
  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', p: 0, overflow: 'hidden' }}>
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY, p: 3, pb: 2 }}>
      MANAGEMENT ACTIONS
    </Typography>
    {rows.length === 0 ? (
      <Typography sx={{ fontSize: '13px', color: TEXT_MUTED, px: 3, pb: 3 }}>No attention items for the selected filters.</Typography>
    ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <Box component="thead">
            <Box component="tr" sx={{ background: NAVY }}>
              {['Priority', 'Issue', 'Impact / Opportunity', 'Recommended Action'].map((h) => (
                <Box
                  component="th"
                  key={h}
                  sx={{ color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'left', p: 1.5, letterSpacing: '0.02em' }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, idx) => (
              <Box component="tr" key={row.title} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }}>
                  <Box
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', background: row.color, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                    }}
                  >
                    {idx + 1}
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1.5, fontSize: '13px', color: TEXT_PRIMARY, verticalAlign: 'top' }}>{row.title}</Box>
                <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.color }}>{row.badges[0].lines[0]}</Typography>
                  <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>{row.badges[0].label}</Typography>
                </Box>
                <Box component="td" sx={{ p: 1.5, fontSize: '12px', color: TEXT_PRIMARY, verticalAlign: 'top' }}>{row.action}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    )}
  </Box>
);

// One labeled row: a thin Actual bar stacked over a thin Potential bar,
// each against the same 0-100% track, with the value printed at its end.
function OeeMeterRow({ label, actual, potential }) {
  const track = { flex: 1, height: 8, borderRadius: 4, background: '#eceff3', position: 'relative', overflow: 'hidden' };
  const fill = (pct, color) => ({
    position: 'absolute', top: 0, left: 0, height: '100%',
    width: `${Math.max(0, Math.min(100, pct))}%`, borderRadius: 4, background: color,
  });
  return (
    <Box sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: TEXT_PRIMARY, mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={track}><Box sx={fill(actual, NAVY)} /></Box>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: NAVY, minWidth: 42, textAlign: 'right' }}>{actual}%</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={track}><Box sx={fill(potential, COLOR_GREEN)} /></Box>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: COLOR_GREEN, minWidth: 42, textAlign: 'right' }}>{potential}%</Typography>
      </Box>
    </Box>
  );
}

// One machine's OEE - Actual vs Potential card: an improvement-opportunity
// callout (current OEE -> potential OEE, and the delta) plus Actual/Potential
// meter rows for Availability, Performance and OEE. All six numbers come
// straight from getMachineProgramSummary (Machine.service.ts's computeOee -
// see its own comment for the Potential/future_* formulas), nothing
// recomputed here.
function OeeActualVsPotentialCard({ machine }) {
  const availability = Number(machine.availability) || 0;
  const performance = Number(machine.performance) || 0;
  const oee = Number(machine.oee) || 0;
  const futureAvailability = Number(machine.future_availability) || 0;
  const futurePerformance = Number(machine.future_performance) || 0;
  const futureOee = Number(machine.future_oee) || 0;
  const delta = Number((futureOee - oee).toFixed(1));
  const deltaColor = delta > 0 ? COLOR_GREEN : delta < 0 ? COLOR_RED : TEXT_MUTED;

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', p: 3 }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY }}>
        OEE — ACTUAL VS POTENTIAL <span style={{ color: TEXT_MUTED, fontWeight: 600 }}>({machine.machine_name})</span>
      </Typography>
      <Typography sx={{ fontSize: '12px', color: TEXT_MUTED, mt: 0.25 }}>
        Potential = if extra idle time and below-target cycle speed were recovered
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: NAVY }} />
          <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Actual</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: COLOR_GREEN }} />
          <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Potential</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '0 0 240px', background: '#f4f6fb', borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT_MUTED, letterSpacing: '0.03em' }}>
            OEE IMPROVEMENT OPPORTUNITY
          </Typography>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: TEXT_PRIMARY, mt: 0.5 }}>
            {oee}% → {futureOee}%
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: deltaColor, mt: 0.5 }}>
            {delta > 0 ? '+' : ''}{delta} percentage points
          </Typography>
        </Box>
        <Box sx={{ flex: '1 1 320px', minWidth: 260 }}>
          <OeeMeterRow label="Availability" actual={availability} potential={futureAvailability} />
          <OeeMeterRow label="Performance" actual={performance} potential={futurePerformance} />
          <OeeMeterRow label="OEE" actual={oee} potential={futureOee} />
        </Box>
      </Box>
    </Box>
  );
}

// Method 4 overview: aggregates getMachineProgramSummary (already summed per
// machine across every one of its program_transactions - see
// Machine.service.ts) across every selected machine, plus a merged
// getExcessReasonBreakdown across those machines for the top-reason
// attention item. No new backend endpoint - this is a pure client-side
// rollup of data the Table tab already fetches piece by piece.
export default function ProgramTransactionOverviewTab({ filters, periodLabel, customerName }) {
  const [machineSummaries, setMachineSummaries] = useState(null);
  const [topReasons, setTopReasons] = useState([]);
  // Which idle family drives the hero number, the Idle Cost stat card, and
  // the OEE Availability formula below - same "By Reason" (reason-based,
  // non_mandatory_idle_*_net) vs "By Part" (part-based, extra_idle_*_net)
  // toggle as the Table tab. Everything else on this page (Setting Time
  // Over Budget, Cycle Time Extremes, Top Idle Reason/Alarm/Program) is
  // unaffected - those measure something else entirely.
  const [showNonMandatoryNet, setShowNonMandatoryNet] = useState(true);
  const [topAlarm, setTopAlarm] = useState(null);
  const [topIdleProgram, setTopIdleProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const machineNames = filters?.machines || [];

  useEffect(() => {
    if (!filters || machineNames.length === 0) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const [summaries, breakdowns, alarmBreakdowns, topIdlePrograms] = await Promise.all([
          getMachineProgramSummary(machineNames, filters.startTimeMs, filters.endTimeMs),
          Promise.all(
            machineNames.map((m) =>
              getExcessReasonBreakdown(m, filters.startTimeMs, filters.endTimeMs).catch(() => [])
            )
          ),
          Promise.all(
            machineNames.map((m) =>
              getAlarmBreakdown(m, filters.startTimeMs, filters.endTimeMs).catch(() => [])
            )
          ),
          Promise.all(
            machineNames.map((m) =>
              getTopIdleProgram(m, filters.startTimeMs, filters.endTimeMs).catch(() => null)
            )
          ),
        ]);

        if (cancelled) return;

        const mergedReasons = new Map();
        breakdowns.flat().forEach((r) => {
          const key = String(r.name || '').trim().toLowerCase();
          if (!key) return;
          const entry = mergedReasons.get(key) || { name: r.name, excess: 0, idle_cost: 0 };
          entry.excess += Number(r.excess) || 0;
          entry.idle_cost += Number(r.idle_cost) || 0;
          mergedReasons.set(key, entry);
        });

        const mergedAlarms = new Map();
        alarmBreakdowns.flat().forEach((a) => {
          const key = String(a.name || '').trim().toLowerCase();
          if (!key) return;
          const entry = mergedAlarms.get(key) || { name: a.name, duration: 0, count: 0 };
          entry.duration += Number(a.duration) || 0;
          entry.count += Number(a.count) || 0;
          mergedAlarms.set(key, entry);
        });
        const sortedAlarms = [...mergedAlarms.values()].sort((a, b) => b.duration - a.duration);

        const bestIdleProgram = topIdlePrograms
          .filter(Boolean)
          .reduce((best, p) => (!best || Number(p.excess_reason_time) > Number(best.excess_reason_time) ? p : best), null);

        setMachineSummaries(Array.isArray(summaries) ? summaries : []);
        setTopReasons([...mergedReasons.values()].sort((a, b) => b.excess - a.excess));
        setTopAlarm(sortedAlarms[0] || null);
        setTopIdleProgram(bestIdleProgram);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load Method 4 overview', err);
          setError('Failed to load overview. Please try again.');
          setMachineSummaries(null);
          setTopReasons([]);
          setTopAlarm(null);
          setTopIdleProgram(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, machineNames.join(',')]);

  if (!filters || machineNames.length === 0) {
    return <StatusBox>Select a machine and click Submit to load the overview.</StatusBox>;
  }
  if (loading) {
    return (
      <StatusBox>
        <CircularProgress size={28} />
      </StatusBox>
    );
  }
  if (error) {
    return <StatusBox>{error}</StatusBox>;
  }
  if (!machineSummaries || machineSummaries.length === 0) {
    return <StatusBox>No data for the selected filters.</StatusBox>;
  }

  const totals = machineSummaries.reduce((acc, m) => {
    acc.idle_cost += Number(m.idle_cost) || 0;
    acc.excess_reason_time += Number(m.excess_reason_time) || 0;
    acc.non_mandatory_idle_time_net += Number(m.non_mandatory_idle_time_net) || 0;
    acc.non_mandatory_idle_cost_net += Number(m.non_mandatory_idle_cost_net) || 0;
    acc.extra_idle_time_net += Number(m.extra_idle_time_net) || 0;
    acc.extra_idle_cost_net += Number(m.extra_idle_cost_net) || 0;
    acc.additional_parts += Number(m.additional_parts) || 0;
    acc.part_count += Number(m.part_count) || 0;
    acc.low_cycle += Number(m.low_cycle) || 0;
    acc.normal_cycle += Number(m.normal_cycle) || 0;
    acc.high_cycle += Number(m.high_cycle) || 0;
    acc.setting_time += Number(m.setting_time) || 0;
    acc.extra_setting_time += Number(m.extra_setting_time) || 0;
    acc.total_run_time += Number(m.total_run_time) || 0;
    acc.total_idle_time += Number(m.total_idle_time) || 0;
    acc.total_stop_time += Number(m.total_stop_time) || 0;
    acc.total_alarm_time += Number(m.total_alarm_time) || 0;
    acc.target_output += Number(m.target_output) || 0;
    acc.transaction_count += Number(m.transaction_count) || 0;
    acc.additional_run_time += Number(m.additional_run_time) || 0;
    return acc;
  }, {
    idle_cost: 0, excess_reason_time: 0, additional_parts: 0, part_count: 0,
    low_cycle: 0, normal_cycle: 0, high_cycle: 0, setting_time: 0,
    extra_setting_time: 0, total_run_time: 0, total_idle_time: 0,
    total_stop_time: 0, total_alarm_time: 0, target_output: 0, transaction_count: 0,
    additional_run_time: 0,
    non_mandatory_idle_time_net: 0, non_mandatory_idle_cost_net: 0,
    extra_idle_time_net: 0, extra_idle_cost_net: 0,
  });

  // By Reason vs By Part - see the state's own comment above.
  const idleTimeValue = showNonMandatoryNet ? totals.non_mandatory_idle_time_net : totals.extra_idle_time_net;
  const idleCostValue = showNonMandatoryNet ? totals.non_mandatory_idle_cost_net : totals.extra_idle_cost_net;

  const totalCycles = totals.low_cycle + totals.normal_cycle + totals.high_cycle;

  // Same computeOee formula as Machine.service.ts (and the PDF export's own
  // computeCombinedOee) - duplicated here since this is an across-all-
  // selected-machines total, not any single machine's own already-computed
  // oee/future_oee field. Availability = Run Time / Total Time (the
  // standard OEE definition - idle never counts as "available", not even
  // this page's own By Reason/By Part idle figure). Future Availability
  // adds back exactly additional_run_time, not the whole idle bucket.
  const combinedTotalTime = totals.total_run_time + totals.total_idle_time + totals.total_stop_time + totals.total_alarm_time;
  const combinedAvailability = combinedTotalTime > 0 ? clampPercent((totals.total_run_time / combinedTotalTime) * 100) : 0;
  const combinedPerformance = totals.target_output > 0 ? clampPercent((totals.part_count / totals.target_output) * 100) : 0;
  const combinedOee = clampPercent((combinedAvailability / 100) * (combinedPerformance / 100) * 100);
  const combinedFutureAvailability = combinedTotalTime > 0 ? clampPercent(((totals.total_run_time + totals.additional_run_time) / combinedTotalTime) * 100) : 0;
  const combinedFuturePerformance = totals.target_output > 0 ? clampPercent(((totals.part_count + totals.additional_parts) / totals.target_output) * 100) : 0;
  const combinedFutureOee = clampPercent((combinedFutureAvailability / 100) * (combinedFuturePerformance / 100) * 100);
  const oeeImprovementPoints = Number((combinedFutureOee - combinedOee).toFixed(1));

  const topMachine = [...machineSummaries].sort((a, b) => Number(b.excess_reason_time) - Number(a.excess_reason_time))[0];
  const topReason = topReasons[0];

  const attentionRows = [];
  if (topMachine && Number(topMachine.excess_reason_time) > 0) {
    attentionRows.push({
      color: COLOR_RED,
      title: `Highest Excess Reason Time: ${topMachine.machine_name}`,
      description: `${fmtHrs(topMachine.excess_reason_time)} of excess reason time, ${fmtCost(topMachine.idle_cost)} of idle cost across ${topMachine.transaction_count} transaction(s).`,
      badges: [{ label: 'Idle Cost', color: COLOR_RED, lines: [fmtCost(topMachine.idle_cost)] }],
      action: "Investigate this machine's top idle reasons and address the largest recoverable time block first.",
    });
  }
  if (topReason && topReason.excess > 0) {
    attentionRows.push({
      color: COLOR_ORANGE,
      title: `Top Idle Reason: ${topReason.name}`,
      description: `${fmtHrs(topReason.excess)} of excess time across the selected machines, after netting each shift's own threshold.`,
      badges: [{ label: 'Excess Time', color: COLOR_ORANGE, lines: [fmtHrs(topReason.excess)] }],
      action: 'Address this idle reason directly - it is the single biggest recoverable time block across selected machines.',
    });
  }
  if (topAlarm && topAlarm.duration > 0) {
    attentionRows.push({
      color: COLOR_AMBER,
      title: `Top Alarm: ${topAlarm.name}`,
      description: `${secondsToHms(topAlarm.duration)} of alarm time across the selected machines (${topAlarm.count} occurrence(s)).`,
      badges: [{ label: `${topAlarm.count} occurrence(s)`, color: COLOR_AMBER, lines: [secondsToHms(topAlarm.duration)] }],
      action: 'Investigate root cause of the top alarm. Implement preventive maintenance.',
    });
  }
  if (totals.low_cycle + totals.high_cycle > 0) {
    attentionRows.push({
      color: COLOR_BLUE,
      title: 'Cycle Time Extremes',
      description: `${totals.low_cycle + totals.high_cycle} cycles ran outside target speed (${totals.low_cycle} low, ${totals.high_cycle} high) out of ${totalCycles} total.`,
      badges: [{ label: 'Out-of-Range Cycles', color: COLOR_BLUE, lines: [`${totals.low_cycle + totals.high_cycle}`] }],
      action: 'Monitor cycle speed regularly - investigate causes for low-speed runs and quality risk for high-speed runs.',
    });
  }
  if (topIdleProgram && Number(topIdleProgram.excess_reason_time) > 0) {
    attentionRows.push({
      color: COLOR_ORANGE,
      title: `Top Idle Program: ${topIdleProgram.program_number || topIdleProgram.program_name || '—'}`,
      description: `${secondsToHms(topIdleProgram.excess_reason_time)} of excess idle time on ${topIdleProgram.machine_name}, the most of any single program transaction.`,
      badges: [{ label: topIdleProgram.machine_name, color: COLOR_ORANGE, lines: [secondsToHms(topIdleProgram.excess_reason_time)] }],
      action: 'Review this program\'s idle causes and optimize its changeover/run sequence.',
    });
  }
  if (totals.extra_setting_time > 0) {
    attentionRows.push({
      color: COLOR_PURPLE,
      title: 'Setting Time Over Budget',
      description: `${fmtHrs(totals.extra_setting_time)} of setting time beyond the configured setup_time budget across all transactions.`,
      badges: [{ label: 'Extra Setting Time', color: COLOR_PURPLE, lines: [fmtHrs(totals.extra_setting_time)] }],
      action: 'Review setup/changeover procedures to bring setting time within the configured budget.',
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '12px', color: showNonMandatoryNet ? TEXT_MUTED : undefined, fontWeight: showNonMandatoryNet ? 400 : 700 }}>
          By Part
        </Typography>
        <Switch size="small" checked={showNonMandatoryNet} onChange={(e) => setShowNonMandatoryNet(e.target.checked)} />
        <Typography sx={{ fontSize: '12px', color: showNonMandatoryNet ? undefined : TEXT_MUTED, fontWeight: showNonMandatoryNet ? 700 : 400 }}>
          By Reason
        </Typography>
      </Box>

      <RecoveryHero
        totals={totals}
        periodLabel={periodLabel}
        customerName={customerName}
        machines={machineNames}
        idleTimeValue={idleTimeValue}
        idleCostValue={idleCostValue}
        showNonMandatoryNet={showNonMandatoryNet}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard
          icon={<AccessTimeIcon />}
          color={COLOR_RED}
          title={showNonMandatoryNet ? 'Non Mandatory Idle Cost (Net)' : 'Non Mandatory Idle Cost1 (Net)'}
          value={fmtCost(idleCostValue)}
          subtitle={showNonMandatoryNet ? "From excess reason time, net of each reason's threshold" : "From each part's idle beyond target handling time, net of mandatory idle"}
          footnote={fmtHrs(idleTimeValue)}
        />
        <StatCard
          icon={<Inventory2OutlinedIcon />}
          color={COLOR_ORANGE}
          title="Additional Parts"
          value={`${totals.additional_parts} parts`}
          subtitle="Potential output if excess reason time were recovered"
          footnote={`${totals.part_count} actual`}
        />
        <StatCard
          icon={<SpeedIcon />}
          color={COLOR_GREEN}
          title="OEE Improvement Opportunity"
          value={`${combinedOee.toFixed(1)}% → ${combinedFutureOee.toFixed(1)}%`}
          subtitle="If excess reason time were recovered"
          footnote={`${oeeImprovementPoints > 0 ? '+' : ''}${oeeImprovementPoints} percentage points`}
          valueColor={oeeImprovementPoints > 0 ? COLOR_GREEN : oeeImprovementPoints < 0 ? COLOR_RED : TEXT_MUTED}
        />
      </Box>

      <ManagementActionsTable rows={attentionRows} />

      {machineSummaries.map((m) => (
        <OeeActualVsPotentialCard key={m.machine_name} machine={m} />
      ))}
    </Box>
  );
}
